import { includes, head, where, equals, filter, map, pipe, prop, uniqBy, sortBy } from 'ramda';
import Currency from 'utils/Currency';
import moment from 'moment';
import { sum } from 'utils/calculator';
import config from 'utils/config';

import { Company, FINCOM } from 'types/payments';

import {
  statusShortCode,
  codes,
  providers,
  paymentMethods,
  EVOUCHER_DATE_FORMAT,
  methods,
  methodsUnavailableForEHL,
  methodsUnavailableForEHLWithoutLoyalty,
  refundSortOrder,
  VONAGE_ALLOWED_OFFICES,
  VONAGE_THALYS_ALLOWED_OFFICES,
  CUSTOMER_PRESENT_OFFICES
} from 'constants/payment';

const ENABLE_ADDITIONAL_THALYS_AGENT_REFUND_METHODS = config('ENABLE_ADDITIONAL_THALYS_AGENT_REFUND_METHODS') === 'true';

export const customerPresent = ({ offices }) => {
  if (offices.some(({ name }) => [...VONAGE_ALLOWED_OFFICES, ...VONAGE_THALYS_ALLOWED_OFFICES].includes(name))) {
    return false;
  }

  if (offices.some(({ name }) => CUSTOMER_PRESENT_OFFICES.includes(name))) {
    return true;
  }

  return false;
};

export const splitPaymentsList = (paymentsList) =>
  paymentsList.filter(
    (pl) => pl.isSuccessful || pl.paymentStatus === statusShortCode.pending || pl.paymentStatus === statusShortCode.success
  );

export const splitPaymentsSum = (splitPaymentList) => sum(splitPaymentList?.map((sp) => sp?.amount));

export const findS3Refunds = filter(
  where({
    method: equals('refund'),
    paymentStatus: equals(statusShortCode.success),
    refundProcessTimestamp: equals(null)
  })
);

export const calcRefund = pipe(findS3Refunds, map(prop('amount')), Currency.sum);

export const getRefundRefId = pipe(findS3Refunds, head, prop('refId'));

export const getRefundAmount = pipe(findS3Refunds, head, prop('amount'), Math.abs);

export const findSuccessRefundsMadeAgainstRefId = (refId) =>
  pipe(
    filter(
      where({
        reference: includes(refId),
        paymentStatus: equals(statusShortCode.success)
      })
    )
  );

export const calcRefundsMadeAgainstRefId = (refId) => pipe(findSuccessRefundsMadeAgainstRefId(refId), map(prop('amount')), Currency.sum);

export const calcTotalToBeRefunded = (payments, refunds, refId) => {
  const totalS3Refunds = getRefundAmount(payments);
  const partialPayments = calcRefundsMadeAgainstRefId(refId)(refunds);

  return Currency.subtract(totalS3Refunds, partialPayments);
};

export const findUniqueBusinessAccountsFromPayments = (payments) =>
  payments.reduce((acc, payment) => {
    const isBusinessAccountPayment = payment.method === codes.BUSINESS_ACCOUNT;
    const hasReference = Boolean(payment.reference);
    const isPayoutOrBalance = hasReference && (payment.reference.includes('payout') || payment.reference.includes('balance'));

    const businessAccount =
      (hasReference && // sometimes we get empty references
        isBusinessAccountPayment &&
        !isPayoutOrBalance &&
        payment.reference.split('-')[0]) ||
      null;

    if (businessAccount && !acc.includes(businessAccount)) {
      return [...acc, businessAccount];
    }

    return acc;
  }, []);

export const getBusinessAccountPaymentsWithAmounts = (payments) =>
  payments.reduce((acc, payment) => {
    const isBusinessAccountPayment = payment.method === codes.BUSINESS_ACCOUNT;
    const hasReference = Boolean(payment.reference);
    const isPayout = payment.isPayout === true;
    const isBalance = payment.isBalance === true;

    if (!isBusinessAccountPayment || !hasReference || !payment.isSuccessful || isBalance) {
      return acc;
    }

    const accountNumber = isPayout ? payment.externalReference : payment.reference.split('-')[0];

    if (!accountNumber) {
      return acc;
    }

    const existing = acc.find((a) => a.accountNumber === accountNumber);
    const entry = existing || { accountNumber, originalAmount: 0, availableRefundAmount: 0 };

    if (isPayout) {
      entry.availableRefundAmount = Number((entry.availableRefundAmount - (payment.amount || 0)).toFixed(2));
    } else {
      const originalAmount = payment.amountInOriginalCurrency || payment.amount || 0;
      const availableRefundAmount = payment.availableRefundAmount !== null ? payment.availableRefundAmount : originalAmount;

      entry.originalAmount = Number((entry.originalAmount + originalAmount).toFixed(2));
      entry.availableRefundAmount = Number((entry.availableRefundAmount + availableRefundAmount).toFixed(2));
    }

    return existing ? acc : [...acc, entry];
  }, []);

const findUniquePayments = (payments, method) =>
  payments.reduce((acc, payment) => {
    const isValidPaymentMethod = payment.method === method;
    const isUnwantedPayment = payment.isPayout || payment.isBalance || !payment.isSuccessful;
    let findCardByExternalReference;

    if (method === codes.PAYPAL || method === codes.EVOUCHER || method === codes.VOUCHER) {
      findCardByExternalReference = acc.find(({ reference }) => reference === payment.reference);
    } else {
      findCardByExternalReference = acc.find(({ externalReference }) => externalReference === payment.externalReference);
    }
    if (isValidPaymentMethod && !isUnwantedPayment && !findCardByExternalReference) {
      return [...acc, payment];
    }

    return acc;
  }, []);

export const findUniqueCardPayments = (payments) => findUniquePayments(payments, codes.CARD);

export const findUniqueApplePayPayments = (payments) => findUniquePayments(payments, codes.APPLE_PAY);

export const findUniquePaypalPayments = (payments) => findUniquePayments(payments, codes.PAYPAL);

export const findUniqueEvoucherPayments = (payments) => findUniquePayments(payments, codes.EVOUCHER);

export const findUniqueIdealPayments = (payments) => findUniquePayments(payments, codes.IDEAL);

export const findUniqueSofortPayments = (payments) => findUniquePayments(payments, codes.SOFORT);

export const findUniqueSocialMediaPayments = (payments) => findUniquePayments(payments, codes.SOCIAL_MEDIA);

const sortByRefundMethodOrder = sortBy((item) => refundSortOrder.indexOf(item.code));

export const getAvailableRefundMethods = ({ availableMethods, payments, isBookingEHL = false, isThalysAgent = false }) => {
  const refundMethodsToAlwaysDisplay = isBookingEHL
    ? [methods.WRITE_OFF, methods.EVOUCHER]
    : [methods.BACS, methods.BUSINESS_ACCOUNT, methods.WRITE_OFF, methods.EVOUCHER];

  const refundMethodsToNeverDisplay = isBookingEHL ? methodsUnavailableForEHL.concat(codes.REFUND) : [codes.PAPER_VOUCHER, codes.REFUND];

  const refundsAvailableDueToPermissions = availableMethods.filter((method) => refundMethodsToAlwaysDisplay.includes(method.code));
  const refundsAvailableDueToPayments = payments.reduce((newMethods, payment) => {
    if (!refundMethodsToNeverDisplay.includes(payment.method)) {
      return newMethods.concat({
        code: `${providers.NONE}_${payment.method}`
      });
    }

    return newMethods;
  }, []);

  const sortedRefundsDueToPayments = sortByRefundMethodOrder(refundsAvailableDueToPayments);
  const sortedRefundsDueToPermissions = sortByRefundMethodOrder(refundsAvailableDueToPermissions);

  let allRefundMethods = [...sortedRefundsDueToPayments, ...sortedRefundsDueToPermissions];

  if (!ENABLE_ADDITIONAL_THALYS_AGENT_REFUND_METHODS) {
    allRefundMethods = allRefundMethods.filter(
      (refundMethod) => !isThalysAgent || payments.find((p) => `${providers.NONE}_${p.method}` === refundMethod.code)
    );
  }

  const uniqueRefundMethods = uniqBy(prop('code'), allRefundMethods);

  return uniqueRefundMethods.filter((method) => Boolean(paymentMethods[method.code]));
};

// Vouchers currency validation rule is defined as follow in the service
// https://github.com/EurostarDigital/evoucher_service
// GBP voucher => valid market: uk-en
// EUR voucher => valid market: fr-fr, be-fr, nl-nl, be-nl, be-en, rw-en
// USD voucher => valid market: us-en
export const mapVoucherCurrencyToMarket = {
  GBP: 'uk-en',
  EUR: 'fr-fr',
  USD: 'us-en'
};

export const mapVoyagerToEvoucherChannels = (channel) => {
  const availableChannels = { EUROSTAR: 'VOY', EIL: 'VTV', EHL: 'VPV', THI: 'VTV' };

  return availableChannels[channel] || 'VOY';
};

export const voucherExpiryDateInvalid = (voucherExpiryDate) => {
  const dateObject = moment(voucherExpiryDate, EVOUCHER_DATE_FORMAT);

  if (
    !dateObject.isValid() || // check validity
    dateObject < moment() ||
    dateObject > moment().add(12, 'months')
  ) {
    return true;
  }

  return false;
};
export const getAllPaymentsPerMethod = (payments, method) =>
  payments.reduce((acc, payment) => {
    const isValidPaymentMethod = payment.method === method;
    const isUnwantedPayment = payment.isPayout || payment.isBalance || !payment.isSuccessful;

    if (isValidPaymentMethod && !isUnwantedPayment) {
      return [...acc, payment];
    }

    return acc;
  }, []);

export const getTotalAmountPaidByGivenCard = (cardPayments, cardExternalReference) => {
  const paymentsPerCard = cardPayments.filter((cardPayment) => cardPayment.externalReference === cardExternalReference);

  return sum(paymentsPerCard.map((payment) => payment.amount));
};

export const getAllRefundsPerMethod = (payments, method) =>
  payments.filter((payment) => payment.method === method && payment.isBalance && payment.isSuccessful);

export const getAmountToRefundPerTab = (selectedValue, payments) => {
  const paymentCode = Object.keys(methods).find((method) => methods[method] === selectedValue);
  const paymentsOfMethod = getAllPaymentsPerMethod(payments, codes[paymentCode]);
  const refundMethods = getAllRefundsPerMethod(payments, codes[paymentCode]);

  const paymentTotal = [codes.EVOUCHER, codes.VOUCHER].includes(codes[paymentCode])
    ? sum(paymentsOfMethod.map((payment) => payment.amount)) +
      sum(getAllPaymentsPerMethod(payments, codes.PAPER_VOUCHER).map((payment) => payment.amount))
    : sum(paymentsOfMethod.map((payment) => payment.amount));

  const refundTotal = sum(refundMethods.map((refund) => refund.amount));

  return paymentTotal + refundTotal;
};

export const getAmountToRefundPerMethod = (payments, method) => getAmountToRefundPerTab(`NONE_${method}`, payments);

export const getMethodsOfPayment = (payMethods) =>
  payMethods
    .map((paymentMethod) => Object.keys(codes).find((code) => codes[code] === paymentMethod))
    .map((methodOfPayment) => methods[methodOfPayment]);

export const getTabsWithPayment = (tabs, methodsOfPayment) => {
  const replacePaperVoucherByEvoucher = methodsOfPayment.map((method) =>
    method === `NONE_${codes.PAPER_VOUCHER}` ? `NONE_${codes.EVOUCHER}` : method
  );

  return tabs.filter((tab) => replacePaperVoucherByEvoucher.includes(tab.value));
};

export const getRefundTabsForBooking = (refundMethods) => refundMethods.map(({ code }) => paymentMethods[code]);

export const getVoucherTypeForPayment = (id, booking) => booking.data.booking.additionalDetails.find((detail) => detail.itemRef === id);

export const rollbackVoucherPayments = ({ payments = [], bookingReference, currency, rollbackMutation }) => {
  const voucherPayments = payments.filter(({ method }) => method === codes.VOUCHER);
  if (!voucherPayments.length) {
    return Promise.resolve();
  }

  return Promise.all(
    voucherPayments.map((payment) =>
      rollbackMutation({
        variables: {
          voucherCodeLastChars: payment.reference,
          market: mapVoucherCurrencyToMarket[currency],
          bookingReference
        }
      })
    )
  );
};

// Merge passenger and customer arrays, remove duplicate and empty objects
export const getPassengersByUniqueEmails = (customer, passengers) => {
  const { firstName: leadPassengerFirstName, lastName: leadPassengerLastName } = passengers[0];
  const isEmptyObject = Object.keys(customer).length === 0;
  const updatedCustomer =
    isEmptyObject || (customer.lastName && customer.firstName)
      ? customer
      : {
          email: customer.email,
          firstName: leadPassengerFirstName,
          lastName: leadPassengerLastName
        };

  const allUniquePassengers = [...new Map([...passengers, updatedCustomer].map((item) => [item.email, item])).values()];

  return allUniquePassengers.filter((allUniquePassenger) => !(allUniquePassenger && Object.keys(allUniquePassenger).length === 0));
};

export const isBookingEHL = (additionalDetails) =>
  !!additionalDetails?.find((additionalDetail) => additionalDetail?.key === FINCOM && additionalDetail?.value === Company.EHL);

export const paymentsTabsTypesForEHL = (paymentsTabsToDisplay, isPackageBookingOnly) =>
  paymentsTabsToDisplay?.filter((p) =>
    isPackageBookingOnly ? methodsUnavailableForEHLWithoutLoyalty.indexOf(p?.value) < 0 : methodsUnavailableForEHL.indexOf(p?.value) < 0
  );

export const getIsPackageBookingOnly = (additionalDetails) =>
  !!additionalDetails?.find(({ key, value }) => key === 'product_type' && value === 'packages');
